import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import * as React from "react";

export type Attribute = {
  label: string;
  value: React.ReactNode;
};

/**
 * Props for ContentInfoPanel component.
 */
export interface ContentInfoPanelProps {
  /**
   * Vertical alignment of the content info within the panel.
   */
  align?: "top" | "center";

  /**
   * The main title to display in the info panel.
   */
  title: React.ReactNode;

  /**
   * The subtitle or description to display below the title.
   */
  subtitle?: React.ReactNode;

  /**
   * The content to display in the main body of the info panel.
   */
  contentPreview?: React.ReactNode;

  /**
   * The subtitle to display below the content preview.
   */
  previewSubtitle?: React.ReactNode;

  /**
   * The footer to display in the bottom of the info panel.
   */
  attributes?: Array<Attribute>;
}

/**
 * ContentInfoPanel displays a title, subtitle, main content, and optional footer in a styled panel.
 * Intended for use in sidebars, modals, or detail views.
 */
export const ContentInfoPanel: React.FC<ContentInfoPanelProps> = ({
  align = "top",
  title,
  subtitle,
  contentPreview,
  previewSubtitle,
  attributes,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent={align === "top" ? "flex-start" : "center"}
      height="100%"
      overflowY="scroll"
      paddingTop={align === "top" ? "20" : "0"}
    >
      {contentPreview && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          rowGap="16"
          flexShrink={0}
          flexGrow={0}
          marginLeft="20"
          marginRight="20"
          marginBottom="16"
          style={{ minHeight: "148px" }}
        >
          {contentPreview}
          {previewSubtitle && (
            <Text variant="label" size="small" color="subtle">
              {previewSubtitle}
            </Text>
          )}
        </Box>
      )}
      {(title || subtitle) && (
        <Box
          display="flex"
          flexDirection="column"
          rowGap="4"
          flexShrink={0}
          flexGrow={0}
          marginLeft="20"
          marginRight="20"
          marginBottom="16"
        >
          <Text size="large">{title}</Text>
          <Text color="subtle">{subtitle}</Text>
        </Box>
      )}
      {attributes && (
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          overflowY="scroll"
          minHeight="0"
          paddingLeft="20" // padding instead of margin for scrollbar space
          paddingRight="20"
        >
          {attributes?.map((attribute) => (
            <Box
              key={attribute.label}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexShrink={0}
              style={{ height: "42px" }}
            >
              <Text variant="label" color="subtle">
                {attribute.label}
              </Text>
              <Text variant="label">{attribute.value}</Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
